
import React, { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNotificationsList } from "@/hooks/useNotificationsList";
import { formatDate } from "@/utils/formatDate";
import { loadPreferences, savePreferences, mergeWithUrlParams } from "@/utils/notificationPreferences";
import TabHeader from "./tabs/TabHeader";
import NotificationContent from "./tabs/NotificationContent";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { useQueryClient } from "@tanstack/react-query";

// UUID validation helper
const isValidUUID = (str: string | undefined): boolean => {
  if (!str) return false;
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

interface NotificationsViewTabProps {
  onArchiveNotification: (id: string) => void;
  onRestoreNotification: (id: string) => void;
  onDeleteNotification: (id: string) => void;
}

const NotificationsViewTab = ({
  onArchiveNotification,
  onRestoreNotification,
  onDeleteNotification
}: NotificationsViewTabProps) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  // Strictly validate viewMode - only allow "active" or "archived"
  const getValidViewMode = (value: string | null): "active" | "archived" => {
    return value === "archived" ? "archived" : "active";
  };
  
  const [viewMode, setViewMode] = React.useState<"active" | "archived">(() => 
    getValidViewMode(searchParams.get("view"))
  );

  const firstRunRef = React.useRef(true);
  
  const {
    notifications,
    filteredNotifications,
    sortedNotifications,
    isLoading,
    error,
    searchTerm,
    setSearchTerm,
    sortConfig,
    requestSort,
    activeNotificationsCount,
    archivedNotificationsCount,
    unreadNotificationsCount
  } = useNotificationsList(viewMode);

  // URL parameters are now the single source of truth
  // Cache is only used to SAVE preferences, not to RESTORE them on mount

  // When viewMode changes, update the URL and cache
  useEffect(() => {
    // Skip complete first cycle to avoid overriding init-applied params
    if (firstRunRef.current) {
      firstRunRef.current = false;
      return;
    }

    const currentView = getValidViewMode(new URLSearchParams(window.location.search).get("view"));
    // Only update if different
    if (currentView !== viewMode) {
      const latest = new URLSearchParams(window.location.search);
      latest.set("view", viewMode);
      setSearchParams(latest, { replace: true });
    }
    
    // Always save to cache
    savePreferences({ viewMode });
  }, [viewMode, setSearchParams]);

  // Keep local viewMode in sync with URL changes (e.g., back/forward navigation)
  useEffect(() => {
    const validUrlView = getValidViewMode(searchParams.get("view"));
    if (validUrlView !== viewMode) {
      setViewMode(validUrlView);
    }
  }, [searchParams]);

  const handleNotificationClick = (id: string) => {
    const currentParams = new URLSearchParams(searchParams);
    const filterParams = `?${currentParams.toString()}`;
    navigate(`/notification-detail/${id}${filterParams}`);
  };

  // Create wrapper functions that handle the MouseEvent parameter properly
  const handleArchive = (e: React.MouseEvent, id: string) => {
    e.stopPropagation(); // Prevent notification click when clicking archive
    onArchiveNotification(id);
  };

  const handleRestore = (e: React.MouseEvent, id: string) => {
    e.stopPropagation(); // Prevent notification click when clicking restore
    onRestoreNotification(id);
  };

  const handleDelete = (e: React.MouseEvent, id: string) => {
    e.stopPropagation(); // Prevent notification click when clicking delete
    onDeleteNotification(id);
  };

  const handleSendWebhook = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    
    console.log("Manual webhook trigger started for notification:", id);
    
    try {
      const body: any = {
        notification_id: id,
      };
      
      if (isValidUUID(user?.id)) {
        body.triggered_by = user?.id;
        body.triggered_by_name = user?.name;
      }
      
      const { data, error } = await supabase.functions.invoke('send-voorraadverschil-webhook', {
        body
      });

      console.log("Webhook response:", { data, error });

      if (error) {
        throw error;
      }

      if (!data?.success) {
        toast.error("Webhook verzending mislukt", {
          description: data?.body ? `Status: ${data.status} - ${data.body.substring(0, 120)}` : `Status: ${data?.status}`
        });
      } else {
        toast.success("Voorraadverschil webhook verzonden", {
          description: `Status: ${data.status} - Succesvol verzonden`
        });
      }

      // Refresh webhook logs
      queryClient.invalidateQueries({ queryKey: ['webhook-logs', id] });
    } catch (error: any) {
      console.error("Error sending webhook:", error);
      toast.error("Fout bij verzenden webhook", {
        description: error.message || "Onbekende fout"
      });
    }
  };

  if (error) {
    return (
      <Card>
        <div className="pt-6 text-center text-red-500">
          <p>Er is een fout opgetreden bij het laden van de meldingen.</p>
          <p className="text-sm">{(error as Error).message}</p>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <TabHeader
        activeNotificationsCount={activeNotificationsCount}
        archivedNotificationsCount={archivedNotificationsCount}
        viewMode={viewMode}
        setViewMode={setViewMode}
      />
      
      <NotificationContent
        isMobile={isMobile}
        notifications={notifications}
        filteredNotifications={filteredNotifications}
        sortedNotifications={sortedNotifications}
        isLoading={isLoading}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        sortConfig={sortConfig}
        requestSort={requestSort}
        formatDate={formatDate}
        handleNotificationClick={handleNotificationClick}
        handleArchive={handleArchive}
        handleRestore={handleRestore}
        handleDelete={handleDelete}
        handleSendWebhook={handleSendWebhook}
        viewMode={viewMode}
        unreadCount={unreadNotificationsCount}
      />
    </Card>
  );
};

export default NotificationsViewTab;
